/**
 * Service Worker Registration
 * Solo se registra en staging y production, y nunca dentro de Electron
 */

import { isElectron } from "@/services/desktopUpdater";

// Referencia al registration activo para que el update-manager pueda forzar un

// chequeo (checkForWebUpdate) y aplicar el update bajo demanda (applyWebUpdate).

let currentRegistration: ServiceWorkerRegistration | null = null;

export const registerServiceWorker =
  async (): Promise<ServiceWorkerRegistration | null> => {
    // Verificar que el navegador soporte Service Workers

    if (!("serviceWorker" in navigator)) {
      console.warn("[SW] Service Workers no soportados en este navegador");

      return null;
    }

    // Dentro de Electron NO corremos el SW: el shell tiene su propio updater

    // (desktopUpdater) y el push por pushManager no funciona en Chromium

    // embebido. Además desregistramos los que hayan quedado: los shells ya

    // instalados tienen un SW controlando el renderer desde antes de este gate.

    if (isElectron()) {
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();

        // allSettled: esperar cada unregister y no dejar rechazos sin manejar
        // (un fallo puntual no aborta el resto).
        const results = await Promise.allSettled(
          registrations.map((registration) => registration.unregister()),
        );

        results.forEach((result) => {
          if (result.status === "rejected") {
            console.warn(
              "[SW] Electron: fallo desregistrando un service worker",
              result.reason,
            );
          }
        });

        // El SW activo puede seguir controlando ESTA sesión hasta el próximo
        // load (unregister no lo detiene); no forzamos reload — podría cortar
        // un vivo. Desde el próximo boot del shell no hay SW.
        console.log(
          "[SW] Electron: service worker desregistrado (sin efecto sobre la sesión actual; libre en el próximo load)",
        );
      } catch (error) {
        console.warn(
          "[SW] Electron: desregistro de service worker falló",
          error,
        );
      }

      // Nada de este módulo debe operar sobre un registration desregistrado.
      currentRegistration = null;

      return null;
    }

    const isDev = import.meta.env.DEV;

    // En dev NO corremos el SW. Cacheaba bundles viejos (y un SW de prod de un

    // build anterior sigue controlando el origen sirviendo JS/CSS Cache-First),

    // lo que tapaba los cambios ("no cambiaste nada"). Desregistramos cualquier

    // SW existente y limpiamos las cachés de versión para que cada reload sirva

    // assets frescos / HMR de Vite.

    if (isDev) {
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();

        for (const registration of registrations) {
          await registration.unregister();
        }

        if (typeof caches !== "undefined") {
          const keys = await caches.keys();

          await Promise.all(
            keys

              .filter((key) => key.startsWith("eriol-v"))

              .map((key) => caches.delete(key)),
          );
        }

        console.log(
          "[SW] dev: service worker deshabilitado + cachés limpiadas",
        );
      } catch (error) {
        console.warn("[SW] dev: limpieza de service worker falló", error);
      }

      return null;
    }

    try {
      // NO desregistramos el SW previo. El unregister en cada load generaba

      // churn y dejaba la ventana momentáneamente sin controlador; el ciclo de

      // update del browser (byte-compare de /sw.js en cada register()/update())

      // ya instala la versión nueva, que precachea y queda en `waiting` hasta

      // que el usuario acepta (applyWebUpdate) o se cierran todas las pestañas.

      // Como el sw.js nuevo difiere en bytes (CACHE_NAME por deploy), los

      // clientes rotos instalan el SW arreglado en el próximo ciclo igual.

      console.log("[SW] Registrando service worker...");

      const registration = await navigator.serviceWorker.register("/sw.js", {
        scope: "/",
      });

      console.log("[SW] Service Worker registrado correctamente", registration);

      currentRegistration = registration;

      // Escuchar actualizaciones

      registration.addEventListener("updatefound", () => {
        const newWorker = registration.installing;

        if (!newWorker) return;

        newWorker.addEventListener("statechange", () => {
          if (
            newWorker.state === "installed" &&
            navigator.serviceWorker.controller
          ) {
            // Hay una nueva versión precacheada. NO recargamos solo: el

            // update-manager (DesktopUpdatePrompt) muestra el toast/diálogo y

            // recién al aceptar se aplica (applyWebUpdate). Así nunca cortamos

            // una transmisión en vivo con una recarga inesperada.

            console.log(
              "[SW] Nueva versión disponible (esperando confirmación del usuario)",
            );
          }
        });
      });

      // Verificar si hay actualizaciones cada 30 minutos

      setInterval(
        () => {
          registration.update();
        },

        30 * 60 * 1000,
      );

      return registration;
    } catch (error) {
      console.error("[SW] Error al registrar Service Worker:", error);

      return null;
    }
  };

/**
 * Desregistrar el service worker
 */

export const unregisterServiceWorker = async (): Promise<void> => {
  if (!("serviceWorker" in navigator)) {
    return;
  }

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();

    for (const registration of registrations) {
      await registration.unregister();

      console.log("[SW] Service Worker desregistrado");
    }
  } catch (error) {
    console.error("[SW] Error al desregistrar Service Worker:", error);
  }
};

/**
 * Obtener la versión del service worker activo
 */

export const getServiceWorkerVersion = async (): Promise<string | null> => {
  if (!("serviceWorker" in navigator) || !navigator.serviceWorker.controller) {
    return null;
  }

  return new Promise((resolve) => {
    const messageChannel = new MessageChannel();

    messageChannel.port1.onmessage = (event) => {
      resolve(event.data.version || null);
    };

    // TS can't narrow `navigator.serviceWorker.controller` across the

    // top-of-function guard plus the async hop into this callback, so we

    // re-check before posting. Returning `null` here mirrors the early-exit

    // contract above.

    if (!navigator.serviceWorker.controller) {
      resolve(null);

      return;
    }

    navigator.serviceWorker.controller.postMessage({ type: "GET_VERSION" }, [
      messageChannel.port2,
    ]);

    // Timeout después de 1 segundo

    setTimeout(() => resolve(null), 1000);
  });
};

/**
 * Fuerza al SW a chequear si hay un sw.js nuevo (y precachear el bundle). Lo
 * llama el update-manager en cada tick antes de ofrecer "Actualizar".
 */

export const checkForWebUpdate = async (): Promise<void> => {
  try {
    await currentRegistration?.update();
  } catch (error) {
    console.warn("[SW] checkForWebUpdate falló", error);
  }
};

/**
 * Aplica el update web bajo demanda (al aceptar el toast): activa el SW en
 * espera si lo hay y recarga. Nunca se dispara solo → no corta un vivo.
 */

export const applyWebUpdate = (): void => {
  const waiting = currentRegistration?.waiting;

  // Sin SW en espera: no hay nada nuevo que activar → recarga directa.

  if (!waiting) {
    window.location.reload();

    return;
  }

  // Flag para no recargar dos veces (controllerchange + fallback).

  let reloaded = false;

  const doReload = () => {
    if (reloaded) return;

    reloaded = true;

    window.location.reload();
  };

  // Esperamos a que el SW nuevo tome control (clients.claim tras skipWaiting)

  // ANTES de recargar: así el reload ya sirve el bundle nuevo (sin race).

  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.addEventListener("controllerchange", doReload, {
      once: true,
    });
  }

  // Fallback: si controllerchange no llega (p.ej. sin controller previo),

  // recargamos igual a los ~2s.

  setTimeout(doReload, 2000);

  waiting.postMessage({ type: "SKIP_WAITING" });
};

/**
 * Red de seguridad anti "stale chunk". Si el shell viejo (en memoria de un
 * Electron que oculta-al-cerrar, o un index.html viejo) pide un chunk con hash
 * muerto tras un deploy, el server responde 404 / fallback SPA (200 text/html) y
 * el browser rechaza el módulo. Ahí, SOLO si confirmamos un deploy nuevo
 * (version.json != la versión de build corriendo), forzamos UN reload. La
 * confirmación por versión evita recargar por baches de red transitorios.
 */

const CHUNK_ERROR_PATTERNS = [
  "failed to fetch dynamically imported module",

  "error loading dynamically imported module",

  "failed to load module script",

  "expected a javascript-or-wasm module script",

  "is not a valid javascript mime type",

  "importing a module script failed",
];

const RELOAD_GUARD_KEY = "eriol:chunk-reload";

const RELOAD_WINDOW_MS = 60_000;

const RELOAD_MAX = 3;

let reloadScheduled = false;

let staleCheckInFlight = false;

const looksLikeChunkError = (message: unknown): boolean => {
  if (typeof message !== "string") return false;

  const m = message.toLowerCase();

  return CHUNK_ERROR_PATTERNS.some((p) => m.includes(p));
};

const isSameOriginAsset = (src: string): boolean => {
  try {
    const u = new URL(src, window.location.href);

    return (
      u.origin === window.location.origin && u.pathname.includes("/assets/")
    );
  } catch {
    return false;
  }
};

const runningBuildVersion = (): string | null =>
  typeof __APP_BUILD_VERSION__ !== "undefined" ? __APP_BUILD_VERSION__ : null;

/** `true` SOLO si el deploy vivo (version.json) difiere de la versión corriendo. */

const isDeployStale = async (): Promise<boolean> => {
  const running = runningBuildVersion();

  if (!running) return false;

  try {
    const res = await fetch("/version.json", { cache: "no-store" });

    if (!res.ok) return false;

    const data = (await res.json()) as { version?: unknown };

    const remote = typeof data.version === "string" ? data.version : null;

    return remote != null && remote !== running;
  } catch {
    return false;
  }
};

/** Loop-guard backstop: máx RELOAD_MAX reloads dentro de RELOAD_WINDOW_MS. */

const withinReloadBudget = (): boolean => {
  try {
    const now = Date.now();

    const raw = sessionStorage.getItem(RELOAD_GUARD_KEY);

    // Parseo defensivo aparte del try general: JSON corrupto NO debe caer al
    // catch de sessionStorage (que permite el reload y desactivaría el
    // presupuesto justo cuando más importa) — se limpia la clave y se sigue
    // como si no hubiera estado previo.
    let prev: { ts: number; count: number } | null = null;

    if (raw) {
      try {
        prev = JSON.parse(raw) as { ts: number; count: number };
      } catch {
        sessionStorage.removeItem(RELOAD_GUARD_KEY);
      }
    }

    const within = prev != null && now - prev.ts < RELOAD_WINDOW_MS;

    const count = within && prev ? prev.count : 0;

    if (count >= RELOAD_MAX) return false;

    sessionStorage.setItem(
      RELOAD_GUARD_KEY,

      JSON.stringify({ ts: now, count: count + 1 }),
    );

    return true;
  } catch {
    // sessionStorage bloqueado: permitimos (reloadScheduled evita multi-reload).

    return true;
  }
};

const reloadIfDeployStale = (): void => {
  if (reloadScheduled || staleCheckInFlight) return;

  staleCheckInFlight = true;

  void isDeployStale()

    .then((stale) => {
      if (!stale || reloadScheduled) return;

      if (!withinReloadBudget()) {
        console.error(
          "[SW] deploy nuevo pero el reload no lo resuelve; no recargo más",
        );

        return;
      }

      reloadScheduled = true;

      window.location.reload();
    })

    .finally(() => {
      staleCheckInFlight = false;
    });
};

/**
 * Instala los listeners de recuperación. Llamar UNA vez, temprano, solo en la
 * ventana de control (no en el OSR de broadcast) y solo en prod.
 */

export const setupChunkErrorRecovery = (): void => {
  if (typeof window === "undefined") return;

  // Chunks dinámicos (React.lazy / import()): Vite emite vite:preloadError. NO

  // hacemos preventDefault: dejamos que el error llegue al ErrorBoundary (si no

  // hay deploy nuevo, el usuario ve el fallback en vez de una recarga en vano).

  window.addEventListener("vite:preloadError", () => {
    reloadIfDeployStale();
  });

  // Entry chunk (<script type=module>) o modulepreload: error de recurso en fase

  // captura. Solo SCRIPT / LINK (no img/font/css → transitorios, no "módulo JS

  // muerto").

  window.addEventListener(
    "error",

    (event) => {
      const message = (event as ErrorEvent).message || "";

      if (looksLikeChunkError(message)) {
        reloadIfDeployStale();

        return;
      }

      const target = event.target as HTMLElement | null;

      if (!target) return;

      const tag = target.tagName;

      if (tag !== "SCRIPT" && tag !== "LINK") return;

      const src =
        (target as HTMLScriptElement).src ||
        (target as HTMLLinkElement).href ||
        "";

      if (src && isSameOriginAsset(src)) reloadIfDeployStale();
    },

    true,
  );

  // import() sin error-boundary → unhandledrejection.

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;

    const message =
      typeof reason === "string"
        ? reason
        : (reason && (reason as { message?: string }).message) || "";

    if (looksLikeChunkError(message)) reloadIfDeployStale();
  });
};
